/**
 * APK-parity helper for saving/sharing files the POS already generates.
 *
 * Why: Android's Capacitor WebView silently drops the standard
 * `<a download>` + `blob:` URL path, so "Download PDF" no-ops in the APK.
 * This helper reuses the EXACT same Blob the existing PDF API returns —
 * it does NOT create a new PDF system. On the native shell it writes the
 * file with @capacitor/filesystem and hands it to the OS share sheet
 * (save / open / share); on the web it keeps the original browser download
 * behaviour untouched. Any native failure falls back to the browser path.
 */
import { isCapacitorNative } from './platformDetect';

function browserDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const res = reader.result || '';
      const comma = String(res).indexOf(',');
      resolve(comma >= 0 ? String(res).slice(comma + 1) : String(res));
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function saveOrShareBlob(blob, filename) {
  if (isCapacitorNative()) {
    try {
      const { Filesystem, Directory } = await import('@capacitor/filesystem');
      const { Share } = await import('@capacitor/share');
      const base64 = await blobToBase64(blob);
      const written = await Filesystem.writeFile({
        path: filename,
        data: base64,
        directory: Directory.Cache,
        recursive: true,
      });
      await Share.share({ title: filename, url: written.uri });
      return { ok: true, mode: 'native-share' };
    } catch (e) {
      // Native write/share unavailable (old APK) → fall back to browser path.
    }
  }
  browserDownload(blob, filename);
  return { ok: true, mode: 'browser' };
}
